/**
 * lite-youtube-embed
 * Source: https://github.com/paulirish/lite-youtube-embed
 */
class LiteYTEmbed extends HTMLElement {
  connectedCallback() {
    this.videoId = this.getAttribute('videoid');
    let playBtnEl = this.querySelector('.lyt-playbtn,.lty-playbtn');
    this.playLabel = (playBtnEl && playBtnEl.textContent.trim()) || this.getAttribute('playlabel') || 'Play';
    this.dataset.title = this.getAttribute('title') || '';

    if (!this.style.backgroundImage) {
      this.style.backgroundImage = `url("https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg")`;
      this.upgradePosterImage();
    }

    if (!playBtnEl) {
      playBtnEl = document.createElement('button');
      playBtnEl.type = 'button';
      playBtnEl.classList.add('lyt-playbtn', 'lty-playbtn');
      this.append(playBtnEl);
    }

    if (!playBtnEl.textContent) {
      const playBtnLabelEl = document.createElement('span');
      playBtnLabelEl.className = 'lyt-visually-hidden';
      playBtnLabelEl.textContent = this.playLabel;
      playBtnEl.append(playBtnLabelEl);
    }

    this.addNoscriptIframe();

    if (playBtnEl.nodeName === 'A') {
      playBtnEl.removeAttribute('href');
      playBtnEl.setAttribute('tabindex', '0');
      playBtnEl.setAttribute('role', 'button');
      playBtnEl.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.activate();
        }
      });
    }

    this.addEventListener('pointerover', LiteYTEmbed.warmConnections, { once: true });
    this.addEventListener('focusin', LiteYTEmbed.warmConnections, { once: true });
    this.addEventListener('click', this.activate);
    this.needsYTApi = this.hasAttribute('js-api') || navigator.vendor.includes('Apple') || navigator.userAgent.includes('Mobi');
  }

  static addPrefetch(kind, url, as) {
    const linkEl = document.createElement('link');
    linkEl.rel = kind;
    linkEl.href = url;
    if (as) linkEl.as = as;
    document.head.append(linkEl);
  }

  static warmConnections() {
    if (LiteYTEmbed.preconnected) return;
    LiteYTEmbed.addPrefetch('preconnect', 'https://www.youtube-nocookie.com');
    LiteYTEmbed.addPrefetch('preconnect', 'https://www.google.com');
    LiteYTEmbed.addPrefetch('preconnect', 'https://googleads.g.doubleclick.net');
    LiteYTEmbed.addPrefetch('preconnect', 'https://static.doubleclick.net');
    LiteYTEmbed.preconnected = true;
  }

  fetchYTPlayerApi() {
    if (window.YT && window.YT.Player) {
      this.ytApiPromise = Promise.resolve();
      return;
    }

    this.ytApiPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onload = () => window.YT.ready(resolve);
      script.onerror = reject;
      this.append(script);
    });
  }

  async getYTPlayer() {
    if (!this.playerPromise) await this.activate();
    return this.playerPromise;
  }

  async addYTPlayerIframe() {
    this.fetchYTPlayerApi();
    await this.ytApiPromise;
    const videoPlaceholderEl = document.createElement('div');
    this.append(videoPlaceholderEl);
    const paramsObj = Object.fromEntries(this.getParams().entries());

    this.playerPromise = new Promise((resolve) => {
      const player = new window.YT.Player(videoPlaceholderEl, {
        width: '100%',
        videoId: this.videoId,
        playerVars: paramsObj,
        events: {
          onReady: (event) => {
            event.target.playVideo();
            resolve(player);
          },
        },
      });
    });
  }

  addNoscriptIframe() {
    const iframeEl = this.createBasicIframe();
    const noscriptEl = document.createElement('noscript');
    noscriptEl.innerHTML = iframeEl.outerHTML;
    this.append(noscriptEl);
  }

  getParams() {
    const params = new URLSearchParams(this.getAttribute('params') || []);
    params.append('autoplay', '1');
    params.append('playsinline', '1');
    return params;
  }

  async activate() {
    if (this.classList.contains('lyt-activated')) return;
    this.classList.add('lyt-activated');

    if (this.needsYTApi) return this.addYTPlayerIframe();

    const iframeEl = this.createBasicIframe();
    this.append(iframeEl);
    iframeEl.focus();
  }

  createBasicIframe() {
    const iframeEl = document.createElement('iframe');
    iframeEl.width = 560;
    iframeEl.height = 315;
    iframeEl.title = this.playLabel;
    iframeEl.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
    iframeEl.allowFullscreen = true;
    iframeEl.referrerPolicy = 'strict-origin-when-cross-origin';
    iframeEl.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(this.videoId)}?${this.getParams().toString()}`;
    return iframeEl;
  }

  upgradePosterImage() {
    window.setTimeout(() => {
      const webpUrl = `https://i.ytimg.com/vi_webp/${this.videoId}/sddefault.webp`;
      const image = new Image();
      image.fetchPriority = 'low';
      image.referrerPolicy = 'origin';
      image.src = webpUrl;
      image.onload = (event) => {
        const noAvailablePoster = event.target.naturalHeight === 90 && event.target.naturalWidth === 120;
        if (!noAvailablePoster) this.style.backgroundImage = `url("${webpUrl}")`;
      };
    }, 100);
  }
}

if (!customElements.get('lite-youtube')) customElements.define('lite-youtube', LiteYTEmbed);
